import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/UserAvatar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Trophy, Users, Clock, Loader2, Check, CheckCircle2, Zap, Crown, Medal, BarChart3,
} from 'lucide-react';
import { ICONS, metricIcon, unitForMetric, fmtNum, ACCENT_FALLBACK } from '@/lib/gamify';

const monthLabel = (iso, t) => {
  if (!iso) return '';
  const parts = iso.split('-');
  const months = t('datetime.monthsShort', { returnObjects: true }) || [];
  const idx = parseInt(parts[1], 10) - 1;
  return `${months[idx] || ''} ${parts[0]}`;
};

const RankBadge = ({ rank }) => {
  if (rank === 1) return <span className="w-7 h-7 rounded-full bg-[#FFB020]/15 text-[#FFB020] flex items-center justify-center"><Crown className="w-4 h-4" /></span>;
  if (rank === 2) return <span className="w-7 h-7 rounded-full bg-white/10 text-white/80 flex items-center justify-center"><Medal className="w-4 h-4" /></span>;
  if (rank === 3) return <span className="w-7 h-7 rounded-full bg-[#FF6B35]/15 text-[#FF6B35] flex items-center justify-center"><Medal className="w-4 h-4" /></span>;
  return <span className="w-7 h-7 rounded-full bg-white/5 text-muted-foreground font-data text-sm flex items-center justify-center">{rank}</span>;
};

const LeaderboardDialog = ({ challenge }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const unit = t(`gamify.units.${unitForMetric(challenge.metric)}`);

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/challenges/${challenge.id}/leaderboard`);
      setData(data);
    } catch (e) {
      toast.error(apiError(e, t('gamify.challenges.toast.loadError')));
    } finally {
      setLoading(false);
    }
  }, [challenge.id, t]);

  useEffect(() => { if (open) fetchBoard(); }, [open, fetchBoard]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="border-white/15 hover:bg-white/10 rounded-lg" data-testid={`leaderboard-btn-${challenge.id}`}>
          <BarChart3 className="w-4 h-4 mr-1" /> {t('gamify.challenges.leaderboard')}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#12161B] border-white/15 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-[#FFB020]" /> {t(`gamify.challenges.defs.${challenge.key}.title`)}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#00B4D8]" /></div>
        ) : (
          <div data-testid={`leaderboard-${challenge.id}`}>
            {data?.my_rank && (
              <div className="text-sm text-muted-foreground mb-3">
                {t('gamify.challenges.myRank')}: <span className="text-white font-data font-semibold">#{data.my_rank}</span> / {data.total}
              </div>
            )}
            {(!data || data.leaderboard.length === 0) ? (
              <p className="text-sm text-muted-foreground py-6 text-center">{t('gamify.challenges.emptyLeaderboard')}</p>
            ) : (
              <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                {data.leaderboard.map((r) => (
                  <div key={r.user_id}
                    className={`flex items-center gap-3 p-2 rounded-xl border ${r.is_me ? 'bg-[#00B4D8]/10 border-[#00B4D8]/30' : 'bg-white/[0.03] border-white/5'}`}
                    data-testid={`lb-row-${r.rank}`}>
                    <RankBadge rank={r.rank} />
                    <UserAvatar src={r.avatar} name={r.name} size={32} />
                    <span className="flex-1 truncate text-sm font-medium">{r.name}{r.is_me && <span className="text-[#48CAE4]"> · {t('gamify.challenges.rider')}</span>}</span>
                    <span className="font-data text-sm" style={{ color: challenge.accent || ACCENT_FALLBACK }}>{fmtNum(r.progress)} <span className="text-muted-foreground">{unit}</span></span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ChallengeCard = ({ challenge, onJoin, onLeave, busy }) => {
  const { t } = useTranslation();
  const Icon = ICONS[challenge.icon] || metricIcon(challenge.metric);
  const accent = challenge.accent || ACCENT_FALLBACK;
  const unit = t(`gamify.units.${unitForMetric(challenge.metric)}`);
  const ended = challenge.days_left <= 0;

  return (
    <div className="kite-card p-5 flex flex-col" data-testid={`challenge-card-${challenge.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1f` }}>
            <Icon className="w-5 h-5" style={{ color: accent }} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold truncate">{t(`gamify.challenges.defs.${challenge.key}.title`)}</div>
            <div className="text-xs text-muted-foreground">{monthLabel(challenge.period_start, t)}</div>
          </div>
        </div>
        {challenge.completed && (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#2EE59D]/12 text-[#2EE59D] border border-[#2EE59D]/25" data-testid={`challenge-completed-${challenge.id}`}>
            <CheckCircle2 className="w-3 h-3" /> {t('gamify.challenges.completed')}
          </span>
        )}
      </div>

      <p className="text-sm text-muted-foreground mt-3 flex-1">{t(`gamify.challenges.defs.${challenge.key}.desc`)}</p>

      <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {t('gamify.challenges.participants', { count: challenge.participant_count })}</span>
        <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {ended ? t('gamify.challenges.ended') : t('gamify.challenges.daysLeft', { count: challenge.days_left })}</span>
      </div>

      {/* Target + reward */}
      <div className="flex items-center justify-between gap-2 mt-3">
        <span className="text-xs text-muted-foreground">{t('gamify.challenges.target')}: <span className="text-white font-data">{fmtNum(challenge.target)} {unit}</span></span>
        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#00B4D8]/12 text-[#48CAE4] border border-[#00B4D8]/25">
          <Zap className="w-3 h-3" /> {t('gamify.goals.rewardCredits', { count: challenge.reward_credits })}
        </span>
      </div>

      {/* Progress (if enrolled) */}
      {challenge.enrolled && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-data text-sm" style={{ color: accent }}>{fmtNum(challenge.my_progress)} <span className="text-muted-foreground">/ {fmtNum(challenge.target)} {unit}</span></span>
            <span className="font-data text-xs text-muted-foreground">{challenge.my_pct}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${challenge.my_pct}%`, background: accent, transition: 'width 300ms ease' }} />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-4">
        {challenge.enrolled ? (
          <Button variant="outline" size="sm" onClick={() => onLeave(challenge.id)} disabled={busy}
            className="border-white/15 hover:bg-white/10 rounded-lg" data-testid={`challenge-leave-${challenge.id}`}>
            <Check className="w-4 h-4 mr-1 text-[#2EE59D]" /> {t('gamify.challenges.joined')}
          </Button>
        ) : (
          <Button size="sm" onClick={() => onJoin(challenge.id)} disabled={busy || ended}
            className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-lg" data-testid={`challenge-join-${challenge.id}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('gamify.challenges.join')}
          </Button>
        )}
        <LeaderboardDialog challenge={challenge} />
      </div>
    </div>
  );
};

export default function Challenges() {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/challenges');
      setChallenges(data.challenges || []);
      if (data.recent_completions?.length) {
        data.recent_completions.forEach((c) => {
          const name = t(`gamify.challenges.defs.${c.key}.title`);
          if (c.reward_credits > 0) toast.success(t('gamify.challenges.toast.completedReward', { name, credits: c.reward_credits }));
          else toast.success(t('gamify.challenges.toast.completed', { name }));
        });
        refreshUser();
      }
    } catch (e) {
      toast.error(apiError(e, t('gamify.challenges.toast.loadError')));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const join = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/challenges/${id}/join`);
      toast.success(t('gamify.challenges.toast.joined'));
      await load();
    } catch (e) {
      toast.error(apiError(e, t('gamify.challenges.toast.joinError')));
    } finally {
      setBusyId(null);
    }
  };

  const leave = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/challenges/${id}/leave`);
      toast.success(t('gamify.challenges.toast.left'));
      await load();
    } catch (e) {
      toast.error(apiError(e, t('gamify.challenges.toast.leaveError')));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#00B4D8] animate-spin" /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8" data-testid="challenges-page">
      <div className="label-eyebrow text-[#48CAE4] mb-2">{t('gamify.challenges.eyebrow')}</div>
      <h1 className="font-display text-4xl sm:text-5xl flex items-center gap-3">{t('gamify.challenges.title')} <Trophy className="w-8 h-8 text-[#FFB020]" /></h1>
      <p className="text-muted-foreground mt-1 max-w-2xl">{t('gamify.challenges.subtitle')}</p>

      {challenges.length === 0 ? (
        <div className="kite-card p-12 text-center mt-8" data-testid="challenges-empty">
          <div className="w-14 h-14 rounded-2xl bg-[#FFB020]/12 flex items-center justify-center mx-auto"><Trophy className="w-7 h-7 text-[#FFB020]" /></div>
          <p className="text-muted-foreground mt-4 max-w-md mx-auto">{t('gamify.challenges.empty')}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {challenges.map((c) => (
            <ChallengeCard key={c.id} challenge={c} onJoin={join} onLeave={leave} busy={busyId === c.id} />
          ))}
        </div>
      )}
    </div>
  );
}
